import i18n from '@ohif/i18n';

import { VIEW_SEGMENTATION_CONFIG } from '../../ComponnentsUI/DrawingCanvas/constants/viewSegmentationConfig';

const CLINICAL_NS = 'ClinicalCollaboration';
const VIEWER_NS = 'GeneralViewer';

const DISPLAY_NAME_TO_VIEW_CODE = Object.fromEntries(
  Object.values(VIEW_SEGMENTATION_CONFIG).map((entry) => [
    entry.displayName.toLowerCase(),
    entry.viewCode,
  ])
);

const STATIC_TARGET_KEYS: Record<string, string> = {
  'entire exam': 'targets.study',
  report: 'targets.report',
  'an exam': 'targets.study',
  'a discussion': 'thread',
};

export function resolveViewCode(
  viewName?: string | null,
  viewCode?: string | null
): string | null {
  if (viewCode && VIEW_SEGMENTATION_CONFIG[viewCode]) return viewCode;
  if (!viewName) return null;
  if (VIEW_SEGMENTATION_CONFIG[viewName]) return viewName;
  return DISPLAY_NAME_TO_VIEW_CODE[viewName.toLowerCase()] ?? null;
}

export function localizeViewName(
  viewName?: string | null,
  viewCode?: string | null
): string {
  if (!viewName && !viewCode) return '';
  const code = resolveViewCode(viewName, viewCode);
  if (code) {
    return i18n.t(`viewTag.${code}`, {
      ns: VIEWER_NS,
      defaultValue: viewName ?? code,
    });
  }
  return viewName ?? '';
}

export type CollaborationLocationInput = {
  target_type?: string | null;
  target_label?: string | null;
  view_name?: string | null;
  view_code?: string | null;
  frame_number?: number | null;
};

export function formatCollaborationLocationLabel(
  input: CollaborationLocationInput
): string {
  const targetType = String(input.target_type ?? '').toUpperCase();
  const viewName = input.view_name?.trim() || null;
  const viewCode = input.view_code?.trim() || null;
  const frameNumber =
    input.frame_number != null && Number.isFinite(Number(input.frame_number))
      ? Number(input.frame_number)
      : null;
  const localizedView = localizeViewName(viewName, viewCode);

  if (targetType === 'FRAME' || localizedView || frameNumber != null) {
    if (localizedView && frameNumber != null) {
      return i18n.t('location.viewFrame', {
        ns: CLINICAL_NS,
        view: localizedView,
        frame: frameNumber,
        defaultValue: `${localizedView} — frame ${frameNumber}`,
      });
    }
    if (localizedView) return localizedView;
    if (frameNumber != null) {
      return i18n.t('location.frameOnly', {
        ns: CLINICAL_NS,
        frame: frameNumber,
        defaultValue: `Frame ${frameNumber}`,
      });
    }
  }

  const raw = input.target_label?.trim();
  if (raw) return localizeCollaborationTargetLabel(raw);

  if (targetType === 'REPORT') {
    return i18n.t('targets.report', { ns: CLINICAL_NS, defaultValue: 'Report' });
  }
  if (targetType === 'SERIE') {
    return i18n.t('targets.serie', { ns: CLINICAL_NS, defaultValue: 'Series' });
  }
  return i18n.t('targets.study', { ns: CLINICAL_NS, defaultValue: 'Exam' });
}

/** Localize legacy English labels stored in the DB (e.g. "Femoral View — frame 13"). */
export function localizeCollaborationTargetLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;

  const generalViewerMatch = trimmed.match(
    /^General viewer\s*[—–-]\s*(?:frame|image)\s+(\d+)\s*$/i
  );
  if (generalViewerMatch) {
    return i18n.t('location.generalViewerFrame', {
      ns: CLINICAL_NS,
      frame: parseInt(generalViewerMatch[1], 10),
      defaultValue: trimmed,
    });
  }

  const frameMatch = trimmed.match(/^(.+?)\s*[—–-]\s*frame\s+(\d+)\s*$/i);
  if (frameMatch) {
    const [, viewPart, frameNum] = frameMatch;
    return formatCollaborationLocationLabel({
      view_name: viewPart.trim(),
      frame_number: parseInt(frameNum, 10),
    });
  }

  const frameOnlyMatch = trimmed.match(/^Frame\s+(\d+)\s*$/i);
  if (frameOnlyMatch) {
    return i18n.t('location.frameOnly', {
      ns: CLINICAL_NS,
      frame: parseInt(frameOnlyMatch[1], 10),
      defaultValue: trimmed,
    });
  }

  const seriesMatch = trimmed.match(/^Series:\s*(.+)$/i);
  if (seriesMatch) {
    return i18n.t('location.series', {
      ns: CLINICAL_NS,
      name: seriesMatch[1].trim(),
      defaultValue: trimmed,
    });
  }

  const staticKey = STATIC_TARGET_KEYS[trimmed.toLowerCase()];
  if (staticKey) {
    return i18n.t(staticKey, { ns: CLINICAL_NS, defaultValue: trimmed });
  }

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    return i18n.t('location.unavailable', {
      ns: CLINICAL_NS,
      defaultValue: trimmed,
    });
  }

  return trimmed;
}

export function formatDiscussionLocationLabel(discussion: {
  target_type: string;
  target_label?: string | null;
  target_context?: {
    view_name?: string | null;
    view_code?: string | null;
    frame_number?: number | null;
  } | null;
}): string {
  const ctx = discussion.target_context;
  if (ctx?.view_name || ctx?.view_code || ctx?.frame_number != null) {
    return formatCollaborationLocationLabel({
      target_type: discussion.target_type,
      view_name: ctx.view_name,
      view_code: ctx.view_code,
      frame_number: ctx.frame_number,
      target_label: discussion.target_label,
    });
  }
  if (discussion.target_label?.trim()) {
    return localizeCollaborationTargetLabel(discussion.target_label);
  }
  return formatCollaborationLocationLabel({ target_type: discussion.target_type });
}

export function formatCollaborationRelativeTime(iso: string): string {
  try {
    const date = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (diffMin < 1) {
      return i18n.t('time.now', { ns: CLINICAL_NS, defaultValue: 'now' });
    }
    if (diffMin < 60) {
      return i18n.t('time.minutesShort', { ns: CLINICAL_NS, count: diffMin, defaultValue: '{{count}}m' });
    }
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) {
      return i18n.t('time.hoursShort', { ns: CLINICAL_NS, count: diffH, defaultValue: '{{count}}h' });
    }
    return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
